import { action, computed, observable } from 'mobx';

import { IOrderConfig } from '@estee/elc-layouts';
import { ViewController } from '@estee/elc-service';
import { command, emit, events, serviceCommands } from '@estee/elc-service-bus';

import { AccountOverlay } from '~domain/entities/AccountOverlay';
import { AccountRepository } from '~repositories/AccountRepository';
import { AuthenticatedRepository } from '~repositories/AuthenticatedRepository';
import { AccountSidebarLoadedUseCase } from '~use-cases/AccountSidebarLoadedUseCase';
import { IMenuLink } from '~views/sidebar/menu/Menu';
import { sidebarSections as sdb } from '~views/sidebar/sections';
import { IShowcaseItem } from '~views/sidebar/showcase/IShowcase';

interface IAccountSidebarController {
    accountOverlay: AccountOverlay;
    accountRepository: AccountRepository;
    authenticatedRepository: AuthenticatedRepository;
}

class AccountSidebarController extends ViewController {
    private accountRepository: AccountRepository;
    private authenticatedRepository: AuthenticatedRepository;
    @observable private accountOverlay: AccountOverlay;
    @observable private orderableConfig: IOrderConfig;

    constructor({
        accountOverlay,
        accountRepository,
        authenticatedRepository
    }: IAccountSidebarController) {
        super();
        this.accountOverlay = accountOverlay;
        this.authenticatedRepository = authenticatedRepository;
        this.accountRepository = accountRepository;
    }

    public loaded = async () => {
        await new AccountSidebarLoadedUseCase().execute();
    };

    private setMenuListConfig(menuList: IMenuLink[], keysToRemove: Set<string>) {
        return menuList.filter(({ menuKey }: IMenuLink) => !keysToRemove.has(menuKey));
    }

    public updated = () => {
        if (this.data.isOverlayVisible) {
            emit(events.ACCOUNT_SIDEBAR_OVERLAY_OPENED);
        }
    };

    @computed
    public get accountSidebarConfig() {
        const { showLoyalty } = this.accountRepository;

        const { [sdb.loyalty]: loyaltyTier, ...remainingConfig } = this.orderableConfig;

        if (showLoyalty === false) {
            const menuConfigParams = remainingConfig[sdb.menu]?.params || {};
            const menuList = menuConfigParams?.[sdb.menu] ?? [];

            const keysToRemove = new Set<string>(['sidebarReferAFriend']);
            menuConfigParams[sdb.menu] = this.setMenuListConfig(menuList, keysToRemove);

            return remainingConfig;
        }

        return this.orderableConfig;
    }

    @computed
    public get totalSections() {
        const totalOrderableRegions = Object.keys(this.accountSidebarConfig).length || 1;

        return totalOrderableRegions;
    }

    @action
    public setOrderableConfig = (orderableConfig: IOrderConfig) => {
        this.orderableConfig = orderableConfig;
    };

    public handleMenuLinkOnClick = (menuLinkData: IMenuLink) => {
        const { label } = menuLinkData;
        emit(events.ACCOUNT_SIDEBAR_MENU_LINK_CLICKED, { label });
    };

    public handleShowcaseOnClick = (showcaseData: IShowcaseItem) => {
        const { title, showCaseKey } = showcaseData;
        const analyticsPayload = {
            item_title: title,
            key: showCaseKey
        };
        emit(events.ACCOUNT_SIDEBAR_SHOWCASE_LINK_CLICKED, analyticsPayload);
    };

    @action
    public hideOverlay = () => {
        void command(serviceCommands.ACCOUNT_OVERLAY_HIDE);
    };

    @computed public get data() {
        return {
            isOverlayVisible: this.accountOverlay.isOverlayVisible,
            authState: this.authenticatedRepository.authState
        };
    }
}

export default AccountSidebarController;
